import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { StockStatCard } from './StockStatCard';
import {
  EventAccountStockSection,
  type EventAccountListItem,
} from './EventAccountStockSection';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Search } from 'lucide-react';
import { GetEventQuery } from '@/graphql/queries/getEvent.generated';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { useGetEventFulfillmentDataByFulfillmentStepQuery } from '@/graphql/queries/getEventFulfillmentDataByFulfillmentStep.generated';

interface EventStockOverviewProps {
  event: GetEventQuery['event'] | undefined;
}

// StockEntry amounts are in boxes (not individual containers)
interface StockEntry {
  event_account_id: string;
  container_type_id: number;
  ordered: number; // boxes
  delivered: number; // boxes
  in_stock: number; // boxes
  handed_out: number; // boxes
}

interface ContainerTypeInfo {
  id: number;
  name: string;
  image_url?: string | null;
  packing_unit: number; // number of containers per box
  color_hex_code?: string | null; // hex color code for the container type
}

// Empty box entry (not linked to container type, but has image_url)
// Empty boxes appear in the table like container types but with 0 values everywhere except returned
interface EmptyBoxReturnEntry {
  type: 'empty_box';
  event_account_id: string;
  count: number; // number of empty boxes
  image_url?: string | null;
  name?: string; // optional name/identifier for empty box type
  color_hex_code?: string | null; // hex color code for the empty box
}

type ReturnEntry = EmptyBoxReturnEntry;

// Component to handle individual event account stock
function EventAccountStockSectionWithData({
  eventAccount,
  eventId,
  stockEntries,
  containerTypes,
  returnedEntries,
  isLoading,
}: {
  eventAccount: EventAccountListItem;
  eventId: string;
  stockEntries: StockEntry[];
  containerTypes: ContainerTypeInfo[];
  returnedEntries: ReturnEntry[];
  isLoading: boolean;
}) {
  // Filter stock entries for this event account
  const accountStockEntries = stockEntries.filter(
    (entry) => entry.event_account_id === eventAccount.id,
  );

  // Filter returned entries for this event account
  const accountReturnedEntries = returnedEntries.filter(
    (entry) => entry.event_account_id === eventAccount.id,
  );

  return (
    <EventAccountStockSection
      eventAccount={eventAccount}
      stockEntries={accountStockEntries}
      containerTypes={containerTypes}
      returnedEntries={accountReturnedEntries}
      isLoading={isLoading}
    />
  );
}

export function EventStockOverview({ event }: EventStockOverviewProps) {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchQuery, setSearchQuery] = useState('');
  const isMobile = useIsMobile();

  // Fetch stock data from GraphQL
  const {
    data: stockDataResponse,
    loading: isLoadingAllStock,
    error: stockError,
  } = useGetEventFulfillmentDataByFulfillmentStepQuery({
    variables: { event_id: eventId || '' },
    skip: !eventId,
  });

  // Map GraphQL response to component data structures
  const stockEntries = useMemo<StockEntry[]>(() => {
    if (
      !stockDataResponse?.getEventFulfillmentDataByFulfillmentStep
        ?.stock_entries
    ) {
      return [];
    }
    return stockDataResponse.getEventFulfillmentDataByFulfillmentStep.stock_entries.map(
      (entry) => ({
        event_account_id: entry.event_account_id,
        container_type_id: entry.container_type_id,
        ordered: entry.ordered ?? 0, // ordered is optional, default to 0
        delivered: entry.delivered,
        in_stock: entry.in_stock,
        handed_out: entry.handed_out,
      }),
    );
  }, [stockDataResponse]);

  const containerTypes = useMemo<ContainerTypeInfo[]>(() => {
    if (
      !stockDataResponse?.getEventFulfillmentDataByFulfillmentStep
        ?.container_types
    ) {
      return [];
    }
    return stockDataResponse.getEventFulfillmentDataByFulfillmentStep.container_types.map(
      (ct) => ({
        id: ct.id,
        name: ct.name,
        image_url: ct.image_url ?? null,
        packing_unit: ct.packing_unit ?? 1, // default to 1 if null
        color_hex_code: ct.color_hex_code ?? null,
      }),
    );
  }, [stockDataResponse]);

  const returnedEntries = useMemo<ReturnEntry[]>(() => {
    if (
      !stockDataResponse?.getEventFulfillmentDataByFulfillmentStep
        ?.container_station_returns
    ) {
      return [];
    }
    const entries: ReturnEntry[] = [];
    const returns =
      stockDataResponse.getEventFulfillmentDataByFulfillmentStep
        .container_station_returns;

    returns.forEach((returnEntry) => {
      // All container_station_returns are treated as empty boxes
      // since they don't have container_type_id in the GraphQL schema
      // The 'type' field doesn't indicate empty_box vs single_container
      if (returnEntry.count > 0) {
        entries.push({
          type: 'empty_box' as const,
          event_account_id: returnEntry.event_account_id,
          count: returnEntry.count,
          image_url: returnEntry.image_url ?? null,
          name: returnEntry.name ?? undefined,
          color_hex_code: returnEntry.color_hex_code ?? null,
        });
      }
    });

    return entries;
  }, [stockDataResponse]);

  const eventAccounts = useMemo<EventAccountListItem[]>(
    () =>
      stockDataResponse?.getEventFulfillmentDataByFulfillmentStep
        ?.event_accounts ?? [],
    [stockDataResponse],
  );

  // Filter event accounts based on search query
  const filteredEventAccounts = useMemo(() => {
    if (!searchQuery.trim()) return eventAccounts;
    const query = searchQuery.toLowerCase();
    return eventAccounts.filter(
      (account) =>
        account.name?.toLowerCase().includes(query) ||
        account.id.toLowerCase().includes(query),
    );
  }, [eventAccounts, searchQuery]);

  // Calculate aggregated stats from stock entries (in boxes, not containers)
  const aggregatedStats = useMemo(() => {
    if (!stockEntries || stockEntries.length === 0) {
      return {
        sealedWarehouse: 0,
        handedOut: 0,
        returned: 0,
        atItemLocation: 0,
      };
    }

    let sealedWarehouse = 0;
    let handedOut = 0;
    let returned = 0;
    let atItemLocation = 0;

    // Aggregate from stock_entries (keep in boxes)
    stockEntries.forEach((entry) => {
      // Keep values in boxes
      handedOut += entry.handed_out;
      atItemLocation += entry.in_stock;
      // Sealed warehouse is the total of in_stock (boxes)
      sealedWarehouse += entry.in_stock;
    });

    // Aggregate returned: count empty boxes
    if (returnedEntries) {
      returnedEntries.forEach((entry) => {
        returned += entry.count;
      });
    }

    return {
      sealedWarehouse,
      handedOut,
      returned,
      atItemLocation,
    };
  }, [stockEntries, returnedEntries]);

  return (
    <div
      className={cn(
        'tw:flex tw:flex-col',
        isMobile ? 'tw:gap-6' : 'tw:container tw:gap-8',
      )}
    >
      <h1
        className={cn(
          'tw:font-semibold tw:leading-none tw:tracking-[-0.4px] tw:text-black',
          isMobile ? 'tw:text-[24px]' : 'tw:text-[40px]',
        )}
      >
        Stock overview
      </h1>

      <div
        className={cn(
          'tw:flex tw:flex-col',
          isMobile ? 'tw:gap-6' : 'tw:gap-8',
        )}
      >
        {/* Stat Cards */}
        <div
          className={cn(
            'tw:flex tw:gap-2 tw:items-start',
            isMobile ? 'tw:flex-wrap' : '',
          )}
        >
          {isLoadingAllStock ? (
            <>
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={cn(
                    'tw:bg-white tw:dark:bg-card tw:border tw:border-neutral-200 tw:dark:border-neutral-700 tw:rounded-lg tw:min-h-[96px] tw:p-4 tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-1.5',
                    isMobile
                      ? 'tw:flex-1 tw:min-w-[calc(50%-4px)]'
                      : 'tw:w-[140px]',
                  )}
                >
                  <Skeleton className="tw:h-2.5 tw:w-16 tw:rounded" />
                  <div className="tw:flex tw:gap-2">
                    <Skeleton className="tw:size-[26px] tw:rounded" />
                    <Skeleton className="tw:size-[26px] tw:rounded" />
                  </div>
                  <Skeleton className="tw:h-7 tw:w-10 tw:rounded" />
                </div>
              ))}
            </>
          ) : (
            <>
              <StockStatCard
                type="sealed-warehouse"
                value={aggregatedStats.sealedWarehouse}
                className={
                  isMobile
                    ? 'tw:flex-1 tw:min-w-[calc(50%-4px)]'
                    : 'tw:w-[140px]'
                }
              />
              <StockStatCard
                type="handed-out"
                value={aggregatedStats.handedOut}
                className={
                  isMobile
                    ? 'tw:flex-1 tw:min-w-[calc(50%-4px)]'
                    : 'tw:w-[140px]'
                }
              />
              <StockStatCard
                type="returned"
                value={aggregatedStats.returned}
                className={
                  isMobile
                    ? 'tw:flex-1 tw:min-w-[calc(50%-4px)]'
                    : 'tw:w-[140px]'
                }
              />
              <StockStatCard
                type="at-item-location"
                value={aggregatedStats.atItemLocation}
                className={
                  isMobile
                    ? 'tw:flex-1 tw:min-w-[calc(50%-4px)]'
                    : 'tw:w-[140px]'
                }
              />
            </>
          )}
        </div>

        {/* Search Input */}
        {isLoadingAllStock ? (
          <Skeleton
            className={cn(
              'tw:h-[36px] tw:rounded-md',
              isMobile ? 'tw:w-full' : 'tw:w-[230px]',
            )}
          />
        ) : (
          <div
            className={cn(
              'tw:relative',
              isMobile ? 'tw:w-full' : 'tw:w-[230px]',
            )}
          >
            <Search className="tw:absolute tw:left-3 tw:top-1/2 tw:-translate-y-1/2 tw:size-5 tw:text-neutral-500" />
            <Input
              placeholder="Search event account name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="tw:pl-10 tw:bg-neutral-100 tw:border-neutral-300 tw:min-h-[36px]"
            />
          </div>
        )}

        {/* Event Account Sections */}
        <div className="tw:flex tw:flex-col tw:gap-4">
          {isLoadingAllStock ? (
            <>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="tw:bg-white tw:dark:bg-card tw:border tw:border-neutral-200 tw:dark:border-neutral-700 tw:rounded-lg tw:p-4 tw:flex tw:items-center tw:gap-3"
                >
                  <Skeleton className="tw:size-5 tw:rounded-full tw:shrink-0" />
                  <Skeleton className="tw:h-5 tw:rounded tw:flex-1 tw:max-w-[280px]" />
                </div>
              ))}
            </>
          ) : filteredEventAccounts.length === 0 ? (
            <div className="tw:p-8 tw:text-center tw:text-neutral-500">
              {searchQuery
                ? 'No event accounts found matching your search'
                : 'No event accounts available'}
            </div>
          ) : (
            filteredEventAccounts.map((account) => (
              <EventAccountStockSectionWithData
                key={account.id}
                eventAccount={account}
                eventId={eventId || ''}
                stockEntries={stockEntries}
                containerTypes={containerTypes}
                returnedEntries={returnedEntries}
                isLoading={isLoadingAllStock}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
